import { useEffect, useState } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import { CustomerDashboardResponse, SummaryTile } from "../../types";
import { useFetchUserDashboardQuery } from "../../api";
import {
  DashboardSummaryRow,
  ProgressComponent,
  TopItemsList,
  WashesByDayBarChart,
} from "./fragments";
import {
  formatCurrency,
  getDate,
  getDateRange,
  getInvoiceAmountByDateRange,
  getInvoiceAmountByKey,
  getNumberOfWashesByDate,
  getNumberOfWashesByDateRange,
  getWashesCountInDateRange,
} from "../../utils";
import { useAtom } from "jotai";
import {
  selectedAccountAtom,
  selectedAccountIdAtom,
  userAtom,
} from "../../atoms";
import { Role } from "../../enums";

export const UserDashboard = () => {
  const [user] = useAtom(userAtom);
  const [accountId] = useAtom(selectedAccountIdAtom);
  const [selectedAccount] = useAtom(selectedAccountAtom);
  const fetchDashboardQuery = useFetchUserDashboardQuery(
    user?.orgId || 0,
    accountId || 0,
    undefined,
    undefined,
    !(user?.roleCode === Role.Customer && !accountId),
  );
  const currentDate = getDate("today");
  const yesterday = getDate("yesterday");
  const lastMonth = getDateRange("lastMonth");
  const thisMonth = getDateRange("thisMonth");
  const [dashboardItems, setDashboardItems] =
    useState<CustomerDashboardResponse>();

  useEffect(() => {
    if (fetchDashboardQuery.isSuccess) {
      setDashboardItems(fetchDashboardQuery.data);
    }
  }, [fetchDashboardQuery.isSuccess, fetchDashboardQuery.data]);

  const dashboardSummaryItems: SummaryTile[] = [
    {
      heading: "Washes Today",
      total: getNumberOfWashesByDate(
        dashboardItems?.washesGroupedByDate || [],
        currentDate,
      ).toString(),
      isLoading: fetchDashboardQuery.isLoading,
      footer: (
        <ProgressComponent
          progressText="since last day"
          progress={
            getNumberOfWashesByDate(
              dashboardItems?.washesGroupedByDate || [],
              currentDate,
            ) -
            getNumberOfWashesByDate(
              dashboardItems?.washesGroupedByDate || [],
              yesterday,
            )
          }
        />
      ),
    },
    {
      heading: "Washes This Month",
      total: getWashesCountInDateRange(
        dashboardItems?.washesGroupedByYearMonthAndWeek || {},
        currentDate,
        currentDate,
      ).toString(),
      isLoading: fetchDashboardQuery.isLoading,
      footer: (
        <ProgressComponent
          progressText="since last month"
          progress={
            getNumberOfWashesByDateRange(
              dashboardItems?.washesGroupedByDate || [],
              thisMonth.startDate,
              thisMonth.endDate,
            ) -
            getNumberOfWashesByDateRange(
              dashboardItems?.washesGroupedByDate || [],
              lastMonth.startDate,
              lastMonth.endDate,
            )
          }
        />
      ),
    },
    {
      heading: "Total Invoices",
      total: formatCurrency(
        getInvoiceAmountByDateRange(
          dashboardItems?.invoicesGroupedByDate || [],
          thisMonth.startDate,
          thisMonth.endDate,
        ),
      ),
      isLoading: fetchDashboardQuery.isLoading,
      footer: (
        <ProgressComponent
          progressText="since last month"
          progress={
            getInvoiceAmountByDateRange(
              dashboardItems?.invoicesGroupedByDate || [],
              thisMonth.startDate,
              thisMonth.endDate,
            ) -
            getInvoiceAmountByDateRange(
              dashboardItems?.invoicesGroupedByDate || [],
              lastMonth.startDate,
              lastMonth.endDate,
            )
          }
        />
      ),
    },
    {
      heading: "Unpaid Invoices",
      total:
        "$ " +
        getInvoiceAmountByKey(
          dashboardItems?.unpaidInvoicesGroupedByAccount || [],
          selectedAccount?.accountName || "",
        ),
      isLoading: fetchDashboardQuery.isLoading,
      footer: (
        <ProgressComponent
          progressText="since last month"
          progress={
            getInvoiceAmountByDateRange(
              dashboardItems?.unpaidInvoicesGroupedByAccount || [],
              thisMonth.startDate,
              thisMonth.endDate,
            ) -
            getInvoiceAmountByDateRange(
              dashboardItems?.unpaidInvoicesGroupedByAccount || [],
              lastMonth.startDate,
              lastMonth.endDate,
            )
          }
        />
      ),
    },
  ];

  return (
    <Box
      sx={{
        height: "fit-content",
        minHeight: "90vh",
        display: "flex",
        width: "100%",
        p: 3,
        flexGrow: 1,
        flexDirection: "column",
      }}
    >
      <Grid2
        container
        direction={"row"}
        spacing={2}
        sx={{
          width: "100%",
          height: "100%",
          flexGrow: 0.1,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
          Dashboard
        </Typography>
      </Grid2>
      <Grid2
        container
        direction={"row"}
        spacing={2}
        sx={{
          width: "100%",
          height: "100%",
          flexGrow: 0.1,
        }}
      >
        <DashboardSummaryRow tiles={dashboardSummaryItems} />
      </Grid2>
      <Grid2
        container
        direction={"row"}
        spacing={2}
        sx={{
          width: "100%",
          height: "100%",
          flexGrow: 0.1,
          pt: 2,
        }}
      >
        <Grid2 size={{ lg: 9, md: 8, sm: 8, xs: 12, xl: 9 }}>
          <WashesByDayBarChart
            washData={dashboardItems?.washesGroupedByDate || []}
            label="Washes By Date"
          />
        </Grid2>
        <Grid2 size={{ lg: 3, md: 4, sm: 4, xs: 12, xl: 3 }}>
          <TopItemsList
            title={"Top Washes"}
            items={dashboardItems?.washesGroupedByWashType || []}
            displayKey={"key"}
            displayValue={"numberOfWashes"}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};
